// PrivacySection.jsx
// Settings → Privacy. Links to both documents, and the "forget me" request.
//
// The request is filed through /api/me/data-request, which pins it to the verified
// caller and emails the system administrator. It is tracked in the admin's Data
// requests queue until closed, and its status is shown back here — so the person who
// asked can see it is being handled, not just that an email left.

import React, { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../lib/api';
import { ERASURE_DAYS, erasureMode } from '../legal/legalConfig';
import './UserApprovalPanel.css';
import './DataRequests.css';

const STATUS_LABEL = { open: 'Received', in_progress: 'In progress', closed: 'Completed' };

function fmtDate(iso) {
  return iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '';
}

export default function PrivacySection({ mode = erasureMode() }) {
  const onClose = mode === 'on-close';
  const [request, setRequest]   = useState(undefined);   // undefined = loading, null = none
  const [error, setError]       = useState('');
  const [confirming, setConfirming] = useState(false);
  const [note, setNote]         = useState('');
  const [busy, setBusy]         = useState(false);
  const [notice, setNotice]     = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const res  = await apiFetch('/api/me/data-request');
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
      setRequest(data.request || null);
    } catch (e) {
      setError(e.message);
      setRequest(null);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function submit() {
    setBusy(true);
    setError('');
    try {
      const res  = await apiFetch('/api/me/data-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
      setRequest(data.request);
      setConfirming(false);
      setNote('');
      setNotice(data.alreadyOpen
        ? 'You already have a deletion request in progress — details below.'
        : 'Your request has been logged. We will confirm by email when it is complete.');
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  const open = request && request.status !== 'closed';

  return (
    <div className="uap">
      <h3 className="uap-title">Legal</h3>
      <p className="uap-lede">
        <a href="/#privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
        {' · '}
        <a href="/#terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
      </p>

      <h3 className="uap-title">{onClose ? 'Close my account and delete my data' : 'Delete my data (“forget me”)'}</h3>
      <p className="uap-lede">
        {onClose
          ? 'Your data is kept while your account is active, because the service learns from it. When you are leaving, ask for your account to be closed and your data deleted.'
          : 'Ask for your personal data to be deleted.'} A system administrator tracks the request
        to completion within {ERASURE_DAYS} days, including backups and archives. Data we
        are required by law to keep — for example for tax or regulatory purposes — may be
        retained for that purpose only, and we will tell you what and why.
      </p>

      {error  && <div className="uap-error" role="alert">{error}</div>}
      {notice && <div className="uap-notice">{notice}</div>}

      {request === undefined ? (
        <div className="uap-empty">Loading…</div>
      ) : request && (
        <div className={`dr-status dr-status--${request.status}`}>
          <div><strong>{open ? 'Deletion request' : 'Last deletion request'}:</strong>{' '}
            {STATUS_LABEL[request.status] || request.status}</div>
          <div className="dr-meta">
            Filed {fmtDate(request.created_at)}
            {open ? ` · due by ${fmtDate(request.due_at)}` : ` · completed ${fmtDate(request.closed_at)}`}
          </div>
          {request.status === 'closed' && request.admin_note && (
            <div className="dr-note">{request.admin_note}</div>
          )}
        </div>
      )}

      {request !== undefined && !open && (
        confirming ? (
          <div className="dr-confirm">
            <p>
              <strong>This cannot be undone.</strong> Once the request is completed your
              account and personal data are deleted and you will lose access.
            </p>
            <label className="uap-join-label" htmlFor="dr-note">Anything we should know? (optional)</label>
            <textarea
              id="dr-note"
              className="dr-textarea"
              value={note}
              onChange={e => setNote(e.target.value)}
              maxLength={2000}
              rows={3}
              disabled={busy}
            />
            <div className="uap-join-actions">
              <button type="button" className="uap-btn uap-btn--danger" onClick={submit} disabled={busy}>
                {busy ? 'Sending…' : 'Request deletion'}
              </button>
              <button type="button" className="uap-btn uap-btn--ghost" onClick={() => setConfirming(false)} disabled={busy}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button type="button" className="uap-btn uap-btn--danger" onClick={() => { setConfirming(true); setNotice(''); }}>
            {onClose ? 'Close my account and delete my data' : 'Request deletion of my data'}
          </button>
        )
      )}
    </div>
  );
}
